// What the user might ask next: the buttons above the chat's text box on
// Today. Tapping one sends it.
//
// Two sources. Straight away, a few worked out here from the user's own lists
// (habits they usually do at this time of day and haven't yet, and a question
// fitting the hour). Then the server's (agentOpening), written by Claude from
// a snapshot of the day, which can go further: a task toward a goal at a free
// time, an overdue task moved to a realistic day. Those are asked for once
// per part of the day and kept in this browser. Either way, a suggestion to
// mark a habit done disappears once that habit is done.

import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { FUNCTION_URLS } from '../config/functions';
import { withoutDashes, withoutEmoji } from '../utils/text';
import { authHeaders, clockLine, failed } from './AgentContext';
import { daySnapshot, localDateString } from './tools';

const KEY = 'mind_suggestions';
// Keys the opening message used before these replaced it
const OLD_KEYS = ['mind_opening', 'mind_opening_v2'];
const MAX = 4;

const partOfDay = (now) => {
  const hour = now.getHours();
  return hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
};

// Which set this moment belongs to, e.g. "2026-09-25 morning"
export const suggestionSlot = (now = new Date()) => `${localDateString(now)} ${partOfDay(now)}`;

// After a failed ask (the server busy, the reply unusable, no signal), try
// again this long after, rather than waiting for the next part of the day
export const RETRY_MS = 30 * 60 * 1000;

const sameName = (a, b) => withoutEmoji(a).toLowerCase() === withoutEmoji(b).toLowerCase();

// Only what the buttons can show: up to four with a label and a prompt
export const cleanSuggestions = (data) => (Array.isArray(data?.suggestions) ? data.suggestions : [])
  .filter((s) => typeof s?.label === 'string' && s.label.trim() && typeof s?.prompt === 'string' && s.prompt.trim())
  .slice(0, MAX)
  .map((s) => ({
    label: withoutDashes(withoutEmoji(s.label)),
    prompt: s.prompt.trim(),
    habit: typeof s.habit === 'string' && s.habit.trim() ? s.habit.trim() : null,
  }));

const shiftDay = (day, n) => {
  const [y, m, d] = day.split('-').map(Number);
  return localDateString(new Date(y, m - 1, d + n));
};

// An event's day, from mind's events or Google's
const eventDay = (e) => e.date || String(e.startDate || e.start || '').split('T')[0];

// Habits that fit this part of the day. Evening habits count from 5pm, and
// night ones too, since the evening set is the last of the day
const fitsNow = (habit, part) => {
  const t = habit.timeOfDay;
  if (!t || t === 'anytime') return true;
  if (part === 'evening') return t === 'evening' || t === 'night';
  return t === part;
};

/**
 * Suggestions worked out from the lists alone, for the moment before the
 * server's arrive (or when they can't). The habits here are ones the user
 * usually does: at least three of the last seven days.
 */
export const localSuggestions = (app, now = new Date()) => {
  const today = localDateString(now);
  const part = partOfDay(now);
  const lastWeek = Array.from({ length: 7 }, (_, i) => shiftDay(today, -1 - i));
  const done = (h, day) => !!app.completions?.[h.id]?.[day];

  const habits = (app.habits || [])
    .filter((h) => !done(h, today) && fitsNow(h, part))
    .map((h) => ({ h, days: lastWeek.filter((d) => done(h, d)).length }))
    .filter(({ days }) => days >= 3)
    .sort((a, b) => b.days - a.days)
    .slice(0, 2)
    .map(({ h }) => {
      const name = withoutEmoji(h.name);
      return { label: `Mark ${name} as done`, prompt: `Mark ${name} as done for today.`, habit: h.name };
    });

  const ask = {
    morning: { label: 'Plan my day', prompt: 'Plan my day around my habits, tasks and events.' },
    afternoon: { label: 'How am I doing today?', prompt: 'How am I doing today?' },
    evening: { label: 'Plan tomorrow', prompt: 'Plan tomorrow around my open tasks and habits.' },
  }[part];
  // The score is the figure on show; the last suggestion asks how to lift it
  const week = { label: 'How do I raise my score?', prompt: 'What would raise my productivity score most today?' };

  // Something for the calendar: the rest of today around the next event, a
  // focus block on a clear day, tomorrow's calendar in the evening
  const hm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const nextEvent = [...(app.events || []), ...(app.googleCalendarEvents || [])]
    .filter((e) => eventDay(e) === today && e.startTime && e.startTime > hm)
    .sort((a, b) => a.startTime.localeCompare(b.startTime))[0];
  let calendar;
  if (part === 'evening') {
    calendar = { label: "What's on my calendar tomorrow?", prompt: "What's on my calendar tomorrow?" };
  } else if (nextEvent) {
    const name = withoutEmoji(nextEvent.name || nextEvent.title || 'my next event');
    calendar = { label: `Plan around ${name} at ${nextEvent.startTime}`, prompt: `Plan the rest of my day around ${name} at ${nextEvent.startTime}.` };
  } else {
    calendar = { label: 'Block an hour to focus today', prompt: 'Find a free hour today and add a focus block to my calendar.' };
  }

  return [...habits, { ...ask, habit: null }, { ...calendar, habit: null }, { ...week, habit: null }].slice(0, MAX);
};

// What this browser holds for the slot: { wait, list }. Before a request goes
// out the slot is stored with a retry time, so a reload mid-request or a
// failure doesn't ask again straight away (each ask uses one of the day's
// allowance); a good set is stored over it and kept for the slot
const readCache = (uid, slot) => {
  try {
    const saved = JSON.parse(localStorage.getItem(`${KEY}_${uid}`) || 'null');
    if (saved?.slot !== slot) return { wait: false, list: null };
    const list = cleanSuggestions(saved);
    return { wait: list.length > 0 || Date.now() < (saved.retryAt || 0), list: list.length ? list : null };
  } catch {
    return { wait: false, list: null };
  }
};

const writeCache = (uid, slot, value = {}) => {
  try {
    OLD_KEYS.forEach((k) => localStorage.removeItem(`${k}_${uid}`));
    localStorage.setItem(`${KEY}_${uid}`, JSON.stringify({ slot, ...value }));
  } catch {
    // Private mode: only this session remembers it (`retryAt` below)
  }
};

const fetchSuggestions = async (day) => {
  const url = FUNCTION_URLS.agent.opening;
  if (!url) throw new Error('The assistant is not configured for this build.');
  const response = await fetch(url, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ day, clock: clockLine() }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw failed(data);
  return cleanSuggestions(data);
};

// When each slot may be asked again, for browsers that can't store it
const retryAt = new Map();

/**
 * The suggestions to show now, or [] when `enabled` is false (not Pro, or a
 * conversation is already going). Asks the server only once the user's lists
 * have loaded and hold something: suggestions about an empty day are wrong.
 */
export const useSuggestions = (enabled) => {
  const app = useApp();
  const uid = app.user?.userId;
  const [slot, setSlot] = useState(suggestionSlot);
  const [fromServer, setFromServer] = useState(() => (uid ? readCache(uid, slot).list : null));
  const [tick, setTick] = useState(0);

  // A phone left open overnight, or the app brought back later, moves on to
  // the next part of the day, and a failed ask gets its retry
  useEffect(() => {
    const check = () => {
      setSlot(suggestionSlot());
      setTick((t) => t + 1);
    };
    const id = setInterval(check, 5 * 60 * 1000);
    document.addEventListener('visibilitychange', check);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);

  // The snapshot is read when it's asked for; later changes to the lists
  // don't ask again until the next part of the day (done habits are filtered
  // out below instead). The app can report its data as loaded a moment
  // before the lists arrive, so it also waits for something to be in them
  const hasData = (app.habits?.length || 0) + (app.tasks?.length || 0) + (app.goals?.length || 0) > 0;
  const ready = enabled && !!uid && !app.dataLoading && hasData;
  useEffect(() => {
    if (!uid) return undefined;
    const cached = readCache(uid, slot);
    setFromServer(cached.list);
    const key = `${uid} ${slot}`;
    if (cached.wait || !ready || Date.now() < (retryAt.get(key) || 0)) return undefined;
    const next = Date.now() + RETRY_MS;
    retryAt.set(key, next);
    writeCache(uid, slot, { retryAt: next });
    fetchSuggestions(daySnapshot(app))
      .then((list) => {
        if (!list.length) return;
        writeCache(uid, slot, { suggestions: list });
        // Unless the day has moved on while they were being written
        if (suggestionSlot() === slot) setFromServer(list);
      })
      // The ones worked out here stay until the retry
      .catch((error) => console.warn('[suggestions]', error.message));
    return undefined;
  }, [uid, slot, ready, tick]);

  const today = localDateString();
  return useMemo(() => {
    if (!enabled || app.dataLoading || !hasData) return [];
    const doneToday = (app.habits || []).filter((h) => app.completions?.[h.id]?.[today]);
    const list = fromServer || localSuggestions(app);
    return list.filter((s) => !s.habit || !doneToday.some((h) => sameName(h.name, s.habit)));
    // `app` changes on every write; these are the parts read above
  }, [enabled, app.dataLoading, hasData, app.habits, app.completions, fromServer, today]);
};
